/**
 * EaterSubscriptionPage - Updated subscription tiers
 * 
 * Changes made:
 * - Removed 'starter' tier ($2.99/month)
 * - Added 'free' tier ($0) with 6-month unlock, $5/month cap
 * - Updated Explorer: $7.99/month, 3-month unlock, $10/month cap (was $20)
 * - Updated VIP: $14.99/month, 1-month unlock, $20/month cap (was $50)
 * - Added tier-specific monthly points goals: Free=20k, Explorer=15k, VIP=10k
 * - Centralized tier definitions in SUBSCRIPTION_TIERS constant
 * 
 * Files updated:
 * - src/pages/eater/redeem/EaterSubscriptionPage.tsx (tier definitions, UI, messaging)
 * - src/helpers/billing-helper.ts (PlanName type: removed 'starter', added 'free')
 * - src/pages/restaurant/subscription/RestaurantSubscriptionPage.tsx (default plan updated)
 */

import { Box, Button, Card, CardContent, Chip, CircularProgress, Container, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Typography, styled } from '@mui/material'
import { IconChevronLeft } from '@tabler/icons-react'
import React, { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useAppSelector } from '../../../store'
import { selectEater, setMessage, setOpen, setSeverity, fetchEater } from '../../../store/profileSlice'
import { selectEaterIsSubscribed } from '../../../store/authSlice'
import useAppUtilities from '../../../hooks/useAppUtilities'
import { ROUTES } from '../../../helpers/routes-helper'
import { createCheckoutSession, createBillingPortalSession, PlanName } from '../../../helpers/billing-helper'
import { mapSubscriptionPlanIdToTier } from '../../../helpers/tier-helper'
import { MuiAlert, MuiSnackbar } from '../styled/global.styled'
import { useDevice } from '../../../hooks/useDevice'
import { fetchRewardsCatalog, fetchWalletBalance, RewardItem, WalletBalanceResponse } from '../../../services/rewardsService'
import { redeemReward, payInWallet } from '../../../helpers/wallet-helper'
import { resolveReferral } from '../../../services/referralsService'
import { getReferralAttribution, clearReferralAttribution } from '../../../helpers/referral-attribution-helper'
import { PENDING_REFERRAL_CODE_KEY } from '../../../helpers/static-constants'
import { SUBSCRIPTION_TIERS, SubscriptionTier, getTierConfig } from '../../../config/subscription-tiers'
import { logEvent, timeStart, timeEnd } from '../../../analytics/analytics'
import { infoLog, errorLog } from '../../../utils/log-helper'
import { navigateTo } from '../../../utils/navigation'

const SubscriptionPageWrapper = styled('div')({
  backgroundSize: '60%,40%',
  backgroundPosition: 'top 15px left 0px, top 0px right 0px',
  backgroundImage: `url('/images/about/background_1.png'), url('/images/about/background_2.png')`,
  backgroundRepeat: 'no-repeat',
})

const SUBSCRIPTION_WEB_URL = 'https://www.resactly.com/redeem-points' // TODO: set real URL

// Helper functions for wallet balance
const getWalletAvailableDollars = (b?: WalletBalanceResponse | null): number =>
  ((b?.walletAvailableCents ?? 0) / 100)

const formatUsd = (n: number): string => n.toFixed(2)

// Helper to get periodic reward copy based on tier
const getPeriodicRewardCopy = (tier: SubscriptionTier): string => {
  if (tier.planName === 'explorer') {
    return 'Earn up to $10 every 2 months'
  }
  if (tier.planName === 'vip') {
    return 'Earn up to $20 every 2 months'
  }
  // Free tier
  return `Redeem up to $${tier.monthlyCapDollars}/month in rewards`
}

const EaterSubscriptionPage: React.FC = () => {
  const { dispatch, navigate } = useAppUtilities()
  const { isHybrid } = useDevice()
  const eater = useAppSelector(selectEater)
  const isSubscribed = useAppSelector(selectEaterIsSubscribed)
  
  // Determine currentPlan ONLY from backend truth (subscribed + subscriptionPlanId)
  // If NOT subscribed OR no subscriptionPlanId → currentPlan is null (no "Current Plan" badge)
  const currentPlan: PlanName | null = (() => {
    if (!isSubscribed || !eater?.subscribed || !eater?.subscriptionPlanId) {
      return null // No subscription = no current plan
    }
    const mappedPlanName = mapSubscriptionPlanIdToTier(eater.subscriptionPlanId)
    return mappedPlanName // Will be 'explorer' | 'vip' | null
  })()
  
  // Determine user tier status for rendering logic
  const isPaid = currentPlan === 'explorer' || currentPlan === 'vip'
  const isFree = !isPaid
  
  // Get currentTier object for display (falls back to Free if currentPlan is null)
  const currentTier = getTierConfig(currentPlan)
  
  const [isLoading, setIsLoading] = useState(false)
  const [rewards, setRewards] = useState<RewardItem[]>([])
  const [loadingRewards, setLoadingRewards] = useState(false)
  const [redeemingRewardCode, setRedeemingRewardCode] = useState<string | null>(null)
  const [walletBalance, setWalletBalance] = useState<WalletBalanceResponse | null>(null)
  const [openInstructions, setOpenInstructions] = useState(false)
  const [selectedRewardForInstructions, setSelectedRewardForInstructions] = useState<RewardItem | null>(null)
  const [openCreditsHelp, setOpenCreditsHelp] = useState(false)
  const message = useAppSelector((state) => state.profile.message)
  const severity = useAppSelector((state) => state.profile.severity)
  const openSnackbar = useAppSelector((state) => state.profile.shouldOpen)

  const openSubscriptionWeb = (): void => {
    navigateTo(SUBSCRIPTION_WEB_URL)
  }

  // Use centralized tier definitions
  const tiers = SUBSCRIPTION_TIERS

  const handleSubscribe = async (planName: PlanName): Promise<void> => {
    // Free tier doesn't require checkout (button is disabled anyway)
    if (planName === 'free') {
      return
    }

    // Track subscription_viewed if not already tracked
    logEvent('subscription_viewed', {
      screen_name: 'subscription',
      source: 'in_app',
    })

    if (isHybrid) {
      // Hybrid/mobile app: do NOT run Stripe in-app, send user to web
      openSubscriptionWeb()
      return
    }

    // Web: run Stripe checkout
    setIsLoading(true)
    const subscribeKey = `subscribe_${planName}`
    timeStart(subscribeKey)
    
    const eaterId = eater?.eaterId ?? null
    infoLog('[Subscription] Starting checkout', { eaterId, planName })
    
    logEvent('subscription_started', {
      plan_name: planName,
      screen_name: 'subscription',
    })
    
    try {
      // Get referral attribution from persisted data (preferred) or fallback to resolving pending code
      let referrerEaterId: string | null = null
      let referrerRestaurantId: string | null = null
      
      const attribution = getReferralAttribution()
      if (attribution) {
        // Use persisted attribution
        referrerEaterId = attribution.referrerEaterId ?? null
        referrerRestaurantId = attribution.referrerRestaurantId ?? null
        infoLog('[Subscription] Using persisted referral attribution', {
          eaterId,
          hasReferrerEaterId: !!referrerEaterId,
          hasReferrerRestaurantId: !!referrerRestaurantId,
        })
      } else {
        // Fallback: try to resolve pending referral code if it still exists
        const pendingCode = localStorage.getItem(PENDING_REFERRAL_CODE_KEY)
        if (pendingCode) {
          try {
            const resolved = await resolveReferral(pendingCode)
            referrerEaterId = resolved.referrerEaterId ?? null
            referrerRestaurantId = resolved.referrerRestaurantId ?? null
            infoLog('[Subscription] Resolved referral code for checkout', {
              eaterId,
              hasReferrerEaterId: !!referrerEaterId,
              hasReferrerRestaurantId: !!referrerRestaurantId,
            })
          } catch (error) {
            // If resolution fails, continue without referrer (non-blocking)
            errorLog('[Subscription] Failed to resolve referral code for checkout', error)
          }
        }
      }

      const checkoutUrl = await createCheckoutSession(
        planName,
        eaterId,
        referrerEaterId,
        referrerRestaurantId,
        null, // restaurantId (not used here)
      )
      
      // Track subscription completion
      const duration = timeEnd(subscribeKey)
      infoLog('[Subscription] Checkout session created successfully', {
        eaterId,
        planName,
        durationMs: duration,
        hasReferrerEaterId: !!referrerEaterId,
        hasReferrerRestaurantId: !!referrerRestaurantId,
      })
      
      logEvent('subscription_completed', {
        plan_name: planName,
        duration_ms: duration,
        screen_name: 'subscription',
      })
      
      // Clear attribution after successful checkout session creation
      clearReferralAttribution()
      
      navigateTo(checkoutUrl)
    } catch (error) {
      const duration = timeEnd(subscribeKey)
      errorLog('[Subscription] Failed to create checkout session', {
        eaterId,
        planName,
        durationMs: duration,
        error,
      })
      
      logEvent('subscription_failed', {
        plan_name: planName,
        error_reason: error instanceof Error ? error.message : 'unknown',
        screen_name: 'subscription',
      })
      
      dispatch(setOpen(true))
      dispatch(setMessage('Failed to start checkout. Please try again.'))
      dispatch(setSeverity('error'))
      setIsLoading(false)
      // Do NOT clear attribution on error so user can retry
    }
  }

  const handleManageSubscription = async (): Promise<void> => {
    if (isHybrid) {
      openSubscriptionWeb()
      return
    }
    
    setIsLoading(true)
    try {
      const portalUrl = await createBillingPortalSession()
      // Full page navigation to Stripe Customer Portal
      navigateTo(portalUrl)
    } catch (error: any) {
      errorLog('Error creating billing portal session', error)
      dispatch(setOpen(true))
      dispatch(setMessage('Unable to open billing portal. Please try again.'))
      dispatch(setSeverity('error'))
      setIsLoading(false)
    }
  }

  const handleCloseSnackbar = (): void => {
    dispatch(setOpen(false))
  }

  // Fetch wallet balance whenever eater is loaded (not gated on subscription)
  useEffect(() => {
    if (eater) {
      const loadWalletBalance = async (): Promise<void> => {
        const eaterId = eater.eaterId
        try {
          const balance = await fetchWalletBalance().catch((e) => {
            errorLog('[Wallet] Failed to load wallet balance', { eaterId, error: e })
            return { walletAvailableCents: 0, monthlyLimitCents: 0, alreadyRedeemedCents: 0, pointsBalance: 0 }
          })
          setWalletBalance(balance)
        } catch (error) {
          errorLog('[Wallet] Failed to load wallet balance', { eaterId, error })
        }
      }
      void loadWalletBalance()
    }
  }, [eater])

  // Fetch rewards catalog for all users (not gated on subscription)
  useEffect(() => {
    if (eater) {
      const loadRewards = async (): Promise<void> => {
        const eaterId = eater.eaterId
        try {
          setLoadingRewards(true)
          const catalog = await fetchRewardsCatalog()
          setRewards(catalog.rewards ?? [])
        } catch (error) {
          errorLog('[Rewards] Failed to load rewards catalog', { eaterId, error })
          dispatch(setOpen(true))
          dispatch(setMessage('Unable to load rewards right now. Please try again later.'))
          dispatch(setSeverity('error'))
        } finally {
          setLoadingRewards(false)
        }
      }
      void loadRewards()
    }
  }, [eater, dispatch])

  const handleRedeemReward = async (reward: RewardItem): Promise<void> => {
    if (!reward.canRedeem) return

    const eaterId = eater?.eaterId
    setRedeemingRewardCode(reward.code)
    infoLog('[Rewards] Redeeming reward', { eaterId, rewardCode: reward.code, rewardName: reward.name })
    
    try {
      const result = await redeemReward(reward.code)
      infoLog('[Rewards] Reward redeemed successfully', { eaterId, rewardCode: reward.code, hasClaimUrl: !!result?.claimUrl })
      dispatch(setOpen(true))
      dispatch(setSeverity('success'))
      // Surface the reward in-app: open the claim link directly so it's claimable even if the
      // account email is fake/undeliverable. Fall back to the email message only when no link.
      if (result?.claimUrl) {
        dispatch(setMessage('Reward redeemed! Opening your gift to claim…'))
        navigateTo(result.claimUrl)
      } else {
        dispatch(setMessage('Reward redeemed! Check your email to claim your gift.'))
      }
      // Refetch eater data to update points
      await dispatch(fetchEater())
      // Reload rewards to update canRedeem status
      const catalog = await fetchRewardsCatalog()
      setRewards(catalog.rewards ?? [])
    } catch (error: any) {
      errorLog('[Rewards] Failed to redeem reward', { eaterId, rewardCode: reward.code, error })
      const errorMessage = error?.response?.data?.message || 'Failed to redeem reward. Please try again.'
      dispatch(setOpen(true))
      dispatch(setMessage(errorMessage))
      dispatch(setSeverity('error'))
    } finally {
      setRedeemingRewardCode(null)
    }
  }

  const handlePayInWallet = async (reward: RewardItem): Promise<void> => {
    if (!reward.canRedeem) return

    const eaterId = eater?.eaterId
    setRedeemingRewardCode(reward.code)
    infoLog('[Rewards] Initiating pay-in-wallet', { eaterId, rewardCode: reward.code, rewardName: reward.name })
    
    try {
      const response = await payInWallet(reward.code)
      infoLog('[Rewards] Pay-in-wallet initiated successfully', { eaterId, rewardCode: reward.code, amountCents: response.amountCents })
      // Open payment link in new tab
      navigateTo(response.paymentLinkUrl)
      dispatch(setOpen(true))
      dispatch(setMessage('Opening payment link...'))
      dispatch(setSeverity('info'))
      // Note: We can't easily detect completion, but points will update on next page load
    } catch (error: any) {
      errorLog('[Rewards] Failed to initiate pay-in-wallet', { eaterId, rewardCode: reward.code, error })
      const errorMessage = error?.response?.data?.message || 'Failed to initiate payment. Please try again.'
      dispatch(setOpen(true))
      dispatch(setMessage(errorMessage))
      dispatch(setSeverity('error'))
      setRedeemingRewardCode(null)
    }
  }

  return (
    <SubscriptionPageWrapper>
      <Helmet>
        <title>Resactly | Subscription Plans</title>
      </Helmet>
      <Container component="main" sx={{ flex: 1, py: { xs: 3, md: 5 } }}>
      <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Button
            variant="text"
            startIcon={<IconChevronLeft size={20} />}
            onClick={() => {
              try {
                navigate(-1)
              } catch {
                window.history.back()
              }
            }}
            sx={{ mb: 2, px: 0, textTransform: 'none' }}
          >
            Back
          </Button>
          <Typography variant="h3" component="h1" color="text.primary" gutterBottom>
            {isSubscribed ? 'Subscription & Rewards' : 'Redeem Points'}
          </Typography>
        </Box>

        {/* Monthly Progress and Ways to Earn are now shown in the Points tab (canonical location) */}
        {/* Removed duplicate panels to avoid confusion - users should check the Points tab for progress tracking */}

        {/* Main content */}
        {/* Free users: Show subscription tiers at top */}
        {isFree && (
          <>
            {isHybrid && (
              <>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                  To start or manage your subscription, we&apos;ll open the Resactly web app in your browser.
                </Typography>
                <Button
                  variant="outlined"
                  sx={{ mb: 3 }}
                  onClick={openSubscriptionWeb}
                >
                  Open subscription page on web
                </Button>
              </>
            )}
            {/* Subscription tiers */}
            <Grid container spacing={3} sx={{ mb: 4 }} id="subscription-tiers">
              {tiers.map((tier) => (
                <Grid item xs={12} md={4} key={tier.planName}>
                  <Card
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      borderRadius: '16px',
                      backgroundColor:
                        tier.planName === 'explorer' ? 'secondary.50' : 'background.paper',
                      border:
                        tier.planName === 'explorer'
                          ? `2px solid`
                          : tier.planName === 'free'
                            ? `1px solid`
                            : `1px solid`,
                      borderColor:
                        tier.planName === 'explorer'
                          ? 'primary.main'
                          : 'grey.200',
                      p: 3,
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        boxShadow: 4,
                      },
                    }}
                  >
                    <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: 0 }}>
                      <Box mb={1.5}>
                        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" mb={1}>
                          {tier.tag && (
                            <Chip
                              label={tier.tag}
                              size="small"
                              sx={{
                                backgroundColor:
                                  tier.planName === 'explorer' ? 'primary.50' : tier.planName === 'free' ? 'grey.200' : 'grey.100',
                                color:
                                  tier.planName === 'explorer' ? 'primary.main' : 'text.secondary',
                                fontWeight: 600,
                                fontSize: '0.75rem',
                              }}
                            />
                          )}
                          {tier.planName === 'explorer' && (
                            <Chip
                              label="Best value"
                              size="small"
                              sx={{
                                backgroundColor: 'success.50',
                                color: 'success.main',
                                fontWeight: 600,
                                fontSize: '0.75rem',
                              }}
                            />
                          )}
                        </Box>
                        <Typography variant="h6" component="h3" color="text.primary" fontWeight={600}>
                          {tier.name}
                        </Typography>
                      </Box>
                      <Typography
                        variant="h5"
                        component="div"
                        color="primary.main"
                        fontWeight={700}
                        gutterBottom
                      >
                        {tier.price}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {tier.description}
                      </Typography>
                      {currentPlan === tier.planName && (
                        <Typography variant="body2" color="primary.main" fontWeight={600} sx={{ mb: 3 }}>
                          Unlocks in {tier.lockMonths} {tier.lockMonths === 1 ? 'month' : 'months'} • {getPeriodicRewardCopy(tier)}
                        </Typography>
                      )}
                      <Box component="ul" sx={{ pl: 2, mb: 3, flex: 1 }}>
                        {tier.features.map((feature, index) => (
                          <Box component="li" key={index} sx={{ mb: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                              {feature}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                      {(() => {
                        const isCurrentPlan = currentPlan === tier.planName
                        const isDowngrade = currentPlan && tier.planName === 'free'
                        
                        // Show "Current Plan" badge and "Manage subscription" button ONLY if this is the actual current plan
                        if (isCurrentPlan) {
                          return (
                            <>
                              <Chip
                                label="Current Plan"
                                color="primary"
                                size="small"
                                sx={{ mb: 2, alignSelf: 'flex-start' }}
                              />
                              <Button
                                variant="outlined"
                                color="primary"
                                fullWidth
                                onClick={() => {
                                  void handleManageSubscription()
                                }}
                                disabled={isLoading}
                                sx={{ mt: 'auto' }}
                              >
                                {isLoading ? (
                                  <CircularProgress size={20} />
                                ) : (
                                  'Manage subscription'
                                )}
                              </Button>
                            </>
                          )
                        }
                        
                        if (isDowngrade) {
                          // Don't show button for downgrading to free
                          return null
                        }
                        
                        // Free tier when NOT subscribed: show non-clickable chip instead of button
                        if (tier.planName === 'free' && !currentPlan) {
                          return (
                            <Chip
                              label="Included"
                              color="default"
                              size="small"
                              sx={{ mt: 'auto', alignSelf: 'flex-start' }}
                            />
                          )
                        }
                        
                        // Upgrade or new subscription (paid tiers only)
                        return (
                          <Button
                            variant={tier.planName === 'explorer' ? 'contained' : 'outlined'}
                            color="primary"
                            fullWidth
                            onClick={() => {
                              void handleSubscribe(tier.planName)
                            }}
                            disabled={isLoading}
                            sx={{ mt: 'auto' }}
                          >
                            {isLoading ? (
                              <CircularProgress size={20} />
                            ) : currentPlan ? (
                              `Upgrade to ${tier.name}`
                            ) : (
                              `Subscribe to ${tier.name}`
                            )}
                          </Button>
                        )
                      })()}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </>
        )}

        {/* Rewards Wallet - Always render (shows $0 for free users) */}
        <Card
              sx={{
                mb: 4,
                borderRadius: '16px',
                backgroundColor: 'secondary.50',
                p: 3,
              }}
            >
              <Typography variant="h6" component="h2" color="text.primary" gutterBottom>
                Your Rewards Wallet
              </Typography>
              {isPaid && (
                <Chip 
                  label={`Current plan: ${currentTier.name}`} 
                  size="small" 
                  sx={{ mb: 2 }} 
                />
              )}
              {walletBalance && (
                <>
                  <Typography variant="h5" component="div" color="text.primary" fontWeight={600} gutterBottom>
                    Available credits: ${formatUsd(getWalletAvailableDollars(walletBalance))}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    {getPeriodicRewardCopy(currentTier)}
                  </Typography>
                </>
              )}
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Points unlock rewards. Credits are what you spend.
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Credits are subject to availability.
              </Typography>
              {(!walletBalance || (walletBalance.walletAvailableCents ?? 0) === 0) && (
                <Button
                  variant="text"
                  size="small"
                  sx={{ px: 0, textTransform: 'none', minWidth: 'auto', mb: 1 }}
                  onClick={() => setOpenCreditsHelp(true)}
                >
                  {/* eslint-disable-next-line react/no-unescaped-entities */}
                  Why don't I have credits?
                </Button>
              )}
              
              {/* Contextual status messages */}
              {walletBalance && (() => {
                const walletAvailable = walletBalance.walletAvailableCents ?? 0
                const alreadyRedeemed = walletBalance.alreadyRedeemedCents ?? 0
                const monthlyLimit = walletBalance.monthlyLimitCents ?? 0
                
                if (walletAvailable === 0 && alreadyRedeemed > 0) {
                  return (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      {/* eslint-disable-next-line react/no-unescaped-entities */}
                      You've used your credits. New credits will be released periodically.
                    </Typography>
                  )
                }
                if (walletAvailable === 0 && monthlyLimit > 0 && alreadyRedeemed === 0) {
                  return (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }} data-testid="credits-empty-message">
                      No credits available yet. Earn activity points and stay active to unlock.
                    </Typography>
                  )
                }
                if (walletAvailable > 0) {
                  return (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      You have credits ready to spend.
                    </Typography>
                  )
                }
                return null
              })()}
        </Card>
        
        {/* Available Rewards - Show for all users */}
        <>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 3, display: 'block' }}>
            Points unlock rewards. Credits spend rewards. Points are not cash.
          </Typography>
          {/* Rewards List */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" component="h2" color="text.primary" gutterBottom sx={{ mb: 3 }}>
              Available Rewards
            </Typography>
              {loadingRewards ? (
                <Box display="flex" justifyContent="center" alignItems="center" py={8}>
                  <CircularProgress />
                  <Typography variant="body1" sx={{ ml: 2 }}>
                    Loading rewards...
                  </Typography>
                </Box>
              ) : rewards.length === 0 ? (
                <Card sx={{ p: 4, textAlign: 'center' }}>
                  <Typography variant="body1" color="text.secondary">
                    No rewards available at this time.
                  </Typography>
                </Card>
              ) : (
                <Grid container spacing={3}>
                  {rewards.map((reward) => (
                    <Grid item xs={12} sm={6} md={4} key={reward.code}>
                      <Card
                        sx={{
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          borderRadius: '16px',
                          overflow: 'hidden',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            boxShadow: 4,
                          },
                        }}
                      >
                        {reward.imageUrl && (
                          <Box
                            component="img"
                            src={reward.imageUrl}
                            alt={reward.name}
                            sx={{
                              width: '100%',
                              height: 200,
                              objectFit: 'cover',
                            }}
                          />
                        )}
                        <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: 2 }}>
                          <Typography variant="h6" component="h3" color="text.primary" fontWeight={600} gutterBottom>
                            {reward.name}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ 
                              mb: 2, 
                              flex: 1,
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                            }}
                          >
                            {reward.shortDescription ?? ''}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                            Eligible value: {getPeriodicRewardCopy(currentTier)}
                          </Typography>
                          {reward.usageInstructions && (
                            <Button
                              variant="text"
                              size="small"
                              onClick={() => {
                                setSelectedRewardForInstructions(reward)
                                setOpenInstructions(true)
                              }}
                              sx={{ px: 0, mb: 1, alignSelf: 'flex-start', textTransform: 'none', minWidth: 'auto' }}
                            >
                              How to redeem
                            </Button>
                          )}
                          {(() => {
                            const walletAvailable = (walletBalance?.walletAvailableCents ?? 0)
                            const hasEnoughCredits = walletAvailable >= reward.amountCents
                            const isDisabled = !reward.canRedeem || !hasEnoughCredits

                            // Cash-out requires a subscription; this takes precedence over
                            // credit-based messaging. Gift cards are never gated this way.
                            if (reward.disableReason === 'SUBSCRIPTION_REQUIRED') {
                              return (
                                <Typography variant="caption" color="error" sx={{ mb: 1, display: 'block' }}>
                                  Cash out with Explorer or VIP. Upgrade to cash out, or redeem a gift card instead.
                                </Typography>
                              )
                            }

                            if (isDisabled && !hasEnoughCredits) {
                              if (walletAvailable === 0) {
                                return (
                                  <Typography variant="caption" color="error" sx={{ mb: 1, display: 'block' }}>
                                    No credits available yet. Earn points to unlock credits.
                                  </Typography>
                                )
                              }
                              return (
                                <Typography variant="caption" color="error" sx={{ mb: 1, display: 'block' }}>
                                  Not enough credits in your Rewards Wallet
                                </Typography>
                              )
                            }
                            if (isDisabled && !reward.canRedeem && reward.disableReason) {
                              return (
                                <Typography variant="caption" color="error" sx={{ mb: 1, display: 'block' }}>
                                  {reward.disableReason === 'NO_WALLET_CREDITS'
                                    ? 'No credits available'
                                    : 'Cannot redeem'}
                                </Typography>
                              )
                            }
                            return null
                          })()}
                          <Box display="flex" gap={1} flexDirection="column">
                            {reward.cashLike ? (
                              <Button
                                variant="contained"
                                color="primary"
                                fullWidth
                                onClick={() => {
                                  void handlePayInWallet(reward)
                                }}
                                disabled={(() => {
                                  const walletAvailable = (walletBalance?.walletAvailableCents ?? 0)
                                  return !reward.canRedeem || walletAvailable < reward.amountCents || redeemingRewardCode === reward.code
                                })()}
                              >
                                {redeemingRewardCode === reward.code ? (
                                  <CircularProgress size={20} />
                                ) : reward.disableReason === 'SUBSCRIPTION_REQUIRED' ? (
                                  'Upgrade to cash out'
                                ) : (
                                  'Cash Out'
                                )}
                              </Button>
                            ) : (
                              <Button
                                variant="contained"
                                color="primary"
                                fullWidth
                                onClick={() => {
                                  void handleRedeemReward(reward)
                                }}
                                disabled={(() => {
                                  const walletAvailable = (walletBalance?.walletAvailableCents ?? 0)
                                  return !reward.canRedeem || walletAvailable < reward.amountCents || redeemingRewardCode === reward.code
                                })()}
                              >
                                {redeemingRewardCode === reward.code ? (
                                  <CircularProgress size={20} />
                                ) : (
                                  'Redeem'
                                )}
                              </Button>
                            )}
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}
            </Box>
        </>

        {/* Manage Subscription - Only for paid users */}
        {isPaid && (
          <Card
              sx={{
                borderRadius: '16px',
                backgroundColor: 'background.paper',
                p: 4,
              }}
            >
              <Typography variant="h5" component="h2" color="text.primary" gutterBottom sx={{ mb: 2 }}>
                Manage Subscription
              </Typography>
              <Box display="flex" gap={2} flexDirection={{ xs: 'column', sm: 'row' }}>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => {
                    void handleManageSubscription()
                  }}
                  disabled={isLoading}
                  sx={{ flex: 1 }}
                >
                  {isLoading ? (
                    <CircularProgress size={24} />
                  ) : (
                    'Manage / Cancel'
                  )}
                </Button>
              </Box>
            </Card>
        )}
      </Box>

      <Dialog 
        open={openInstructions} 
        onClose={() => setOpenInstructions(false)} 
        fullWidth 
        maxWidth="sm"
      >
        <DialogTitle>How to redeem</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: 2 }}>
            {selectedRewardForInstructions?.usageInstructions ?? 'No instructions available for this reward.'}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            fullWidth
            onClick={() => {
              setOpenInstructions(false)
              navigate(ROUTES.REDEEM_POINTS)
            }}
          >
            See all redemption options
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenInstructions(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog 
        open={openCreditsHelp} 
        onClose={() => setOpenCreditsHelp(false)} 
        fullWidth 
        maxWidth="sm"
      >
        <DialogTitle>About credits</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" color="text.primary" fontWeight={600} gutterBottom>
              Credits vs Points
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Points track activity and help unlock rewards.
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Credits are used to redeem rewards.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Credits are not cash and are released periodically.
            </Typography>
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" color="text.primary" fontWeight={600} gutterBottom>
              How credits unlock
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              To unlock this month&apos;s credits, earn {currentTier.monthlyGoalPoints.toLocaleString()} activity points this month.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              You must be active in the last 30 days to qualify.
            </Typography>
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" color="text.primary" fontWeight={600} gutterBottom>
              Unlock timing
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Credits unlock after {currentTier.lockMonths} {currentTier.lockMonths === 1 ? 'month' : 'months'} of activity on this tier.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {getPeriodicRewardCopy(currentTier)}.
            </Typography>
            {(currentTier.planName === 'explorer' || currentTier.planName === 'vip') && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                After your first payout, credits are released every 2 months.
              </Typography>
            )}
            {currentTier.planName === 'free' && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Free credits are limited by a global monthly pool. If the pool is exhausted, you may qualify but not receive credits that month.
              </Typography>
            )}
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Credits are released periodically and expire if not redeemed within the release window. Check back regularly so you don&apos;t miss a release.
            </Typography>
          </Box>

          {currentTier.rolloverApplies && currentTier.rolloverPointsCap && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" color="text.primary" fontWeight={600} gutterBottom>
                VIP rollover
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                If you don&apos;t reach the full points goal in a payout cycle, you can roll over up to {currentTier.rolloverPointsCap.toLocaleString()} points of shortfall into the next payout cycle only (one-time carry). This reduces next cycle&apos;s required points. No stacking.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Example: If you earned 8,500 points this cycle, you can roll over 1,500 points (up to 2,000). Next cycle you only need 8,500 points to unlock your full credits.
              </Typography>
            </Box>
          )}

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" color="text.primary" fontWeight={600} gutterBottom>
              Availability
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Credits are subject to availability.
            </Typography>
          </Box>

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2, fontStyle: 'italic' }}>
            We may update eligibility rules as the program improves.
          </Typography>
        </DialogContent>
        <DialogActions>
          {isHybrid ? (
            <Button
              variant="outlined"
              onClick={() => {
                setOpenCreditsHelp(false)
                openSubscriptionWeb()
              }}
            >
              View plans
            </Button>
          ) : (
            <Button
              variant="outlined"
              onClick={() => {
                setOpenCreditsHelp(false)
                const tierSection = document.getElementById('subscription-tiers')
                if (tierSection) {
                  tierSection.scrollIntoView({ behavior: 'smooth' })
                }
              }}
            >
              View plans
            </Button>
          )}
          <Button variant="contained" onClick={() => setOpenCreditsHelp(false)}>
            Got it
          </Button>
        </DialogActions>
      </Dialog>

      <MuiSnackbar
        open={openSnackbar}
        onClose={handleCloseSnackbar}
      >
        <MuiAlert
          onClose={handleCloseSnackbar}
          severity={severity}
        >
          {message}
        </MuiAlert>
      </MuiSnackbar>
    </Container>
    </SubscriptionPageWrapper>
  )
}

export default EaterSubscriptionPage
